import { useEffect, useState, startTransition } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useLoginMutation,
  useLoginWithFirebaseMutation,
  useEmployeeLoginMutation,
  useRequestPasswordResetMutation,
  useRequestEmployeePasswordResetMutation,
} from "../../store/api/usersApiSlice";
import { setCredentials } from "../../store/slices/authSlice";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  Fade,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import {
  Close,
  LocalPhone,
  Email,
  PersonAdd,
  Security,
  Lock,
  AdminPanelSettings,
  Person
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Navigate, Link } from "react-router-dom";
import * as Yup from "yup";
import EmailForm from "./EmailForm";
import PhoneNumberForm from "./PhoneNumberForm";
import { useFirebase } from "../../hooks";
import { Loading } from "../../components";
import theme, { appColors } from "../../constants/theme";
import { reportClientAuthError } from "../../utils/clientAuthErrorReport";
import { isPasswordResetRateLimited } from "../../utils/passwordResetErrors";

const logLoginFailure = (stage, details = {}) => {
  console.error("[Login] Giriş başarısız:", { stage, ...details });
};

const loginMessageKeyFromCode = (code) => {
  if (code === "auth/too-many-requests") return "email_verification.too_many_requests";
  if (code === "auth/network-request-failed") return "email_verification.network_error";
  if (code === "auth/user-disabled") return "email_verification.user_disabled";
  if (code === "auth/invalid-email") return "invalid_email_address";
  return "wrong_email_address_or_password";
};

const Login = ({ superLogin = false }) => {
  //STATES---------------------
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [login] = useLoginMutation();
  const [loginWithFirebase] = useLoginWithFirebaseMutation();
  const [employeeLogin] = useEmployeeLoginMutation();
  const [requestPasswordReset] = useRequestPasswordResetMutation();
  const [requestEmployeePasswordReset] = useRequestEmployeePasswordResetMutation();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { firebaseLogin, getSignature, getFCMToken, sendEmailVerificationFirebase, auth, signInWithEmailAndPassword, signOut } = useFirebase();
  const [showEmailForm, setShowEmailForm] = useState(superLogin); // Email form gösterimi için - SuperLogin'de başlangıçta email formu göster
  const [showCaptcha, setShowCaptcha] = useState(false); // Captcha form alanları doldurulduğunda gösterilecek
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isVerified, setIsVerified] = useState(false); // Captcha doğrulaması için - tüm login türlerinde false
  const [lastEmail, setLastEmail] = useState(""); // Email değişikliğini takip etmek için
  const [lastPassword, setLastPassword] = useState(""); // Password değişikliğini takip etmek için
  const [loginAttempts, setLoginAttempts] = useState(0); // Login deneme sayısı
  const [showVerificationButton, setShowVerificationButton] = useState(false); // Email verification butonu gösterimi
  const [resetPasswordCounter, setResetPasswordCounter] = useState(0); // Başarısız login tetikleyici
  const [forgotOpen, setForgotOpen] = useState(false);
  const [loginErrorKey, setLoginErrorKey] = useState(null);
  const [forgotLoading, setForgotLoading] = useState(false);

  const forgotValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("error_generic_invalid_input"))
      .max(320, t("error_generic_invalid_input"))
      .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, t("error_generic_invalid_input"))
      .required(t("error_generic_invalid_input")),
  });

  const forgotFormik = useFormik({
    initialValues: { email: "" },
    validationSchema: forgotValidationSchema,
    validateOnBlur: true,
    onSubmit: async ({ email }) => {
      try {
        setForgotLoading(true);
        const requestReset = superLogin
          ? requestEmployeePasswordReset
          : requestPasswordReset;
        const body = superLogin
          ? { email }
          : { email, continueUrl: `${window.location.origin}/reset-password` };
        await requestReset(body).unwrap();
        toast.success(t("password_reset_email_sent_generic"));
        setForgotOpen(false);
      } catch (err) {
        if (isPasswordResetRateLimited(err)) {
          toast.error(t("password_reset_rate_limited"));
        } else {
          toast.success(t("password_reset_email_sent_generic"));
          setForgotOpen(false);
        }
      } finally {
        setForgotLoading(false);
      }
    },
  });

  // Sayfa yenilendiğinde verification butonunu gizle
  useEffect(() => {
    setShowVerificationButton(false);
  }, []);

  //FUNCTIONS-------------------
  //Login handler
  const handleLoginEmail = async (values) => {
    if (!superLogin && showCaptcha && !captchaToken) {
      setLoading(false);
      toast.error(t("please_complete_captcha"));
      return;
    }

    setLoading(true);
    let loginObj = {};
    if (!superLogin && showCaptcha && captchaToken) {
      loginObj.captchaToken = captchaToken;
    }

    let failedEndpoint = superLogin
      ? "/api/EmployeeAuth/Login"
      : "/api/DoctorAuth/LoginWithFirebase";
    let authStage = superLogin ? "login" : "loginWithFirebase";

    try {
      let signature = null;
      let firebaseToken = null;
      const deviceToken = await getFCMToken();
      let res;
      if (!superLogin) {
        // Doktor hesapları Firebase'de doğrulanır. Asistanlar Firebase'e bağlı değildir.
        const firebaseRes = await handleFirebaseLogin(values.email, values.password);
        if (firebaseRes?.accessToken) {
          signature = await getSignature(firebaseRes.accessToken);
          firebaseToken = firebaseRes.accessToken;

          loginObj = {
            signature,
            firebaseToken,
            deviceToken,
            email: values.email,
            captchaToken: showCaptcha ? captchaToken : null,
          };
          res = await loginWithFirebase(loginObj).unwrap();
        } else {
          logLoginFailure("firebase_auth", {
            email: values.email,
            superLogin,
            code: firebaseRes?.code,
          });
          failedEndpoint = "/api/DoctorAuth/Login";
          authStage = "login";
          res = await login({
            email: values.email,
            password: values.password,
            captchaToken: showCaptcha ? captchaToken : null,
          }).unwrap();
        }
      } else {
        // Süper login: direkt DB login, Firebase doğrulaması yok.
        loginObj = {
          email: values.email,
          password: values.password,
        };

        res = await employeeLogin(loginObj).unwrap();
      }

      if (res?.isSuccess) {
        // Başarılı login - deneme sayısını sıfırla
        setLoginAttempts(0);
        setShowCaptcha(false);
        setIsVerified(true);
        setCaptchaToken(null);
        setShowVerificationButton(false); // Verification butonunu gizle

        // Login işlemini tamamla
        dispatch(
          setCredentials({
            token: res.data.token,
            userType: res.data.userType,
            timeZone: res.data.timeZone,
            isEmailVerified: res.data.isEmailVerified,
            passwordStatus: res.data.passwordStatus,
            status: res.data.status,
          })
        );

        // Backend'den gelen isEmailVerified alanını kontrol et
        // Email verification durumu artık auth slice'da saklanacak ve SafeRoutes'ta gösterilecek

        // Ana sayfaya yönlendir (CreateFirstLogin durumunda SafeRoutes'ta kontrol edilecek)
        // Parola değiştirme dialog'u SafeRoutes'ta açılacak ve ana sayfa içeriği gösterilmeyecek
        window.location.href = "/";
      } else {
        // Backend login başarısız
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        if (newAttempts >= 5) {
          setShowCaptcha(true);
        }

        const msgs = Object.values(res.error.messages || {});
        const isCaptchaError = msgs.some(m =>
          m.toLowerCase().includes("captcha")
        ) || res.error?.messages?.["GNR-00027"];

        const backendMsgs = res.error?.messages || {};
        const backendText = Object.values(backendMsgs).filter(Boolean).join("; ");
        logLoginFailure("backend_rejected", {
          email: values.email,
          superLogin,
          isSuccess: res.isSuccess,
          error: res.error,
          messages: backendMsgs,
          message: backendText,
        });
        reportClientAuthError({
          provider: "Backend",
          stage: authStage,
          failedEndpoint,
          identifier: values.email ? String(values.email).toLowerCase().trim() : null,
          errorCode: Object.keys(backendMsgs)[0] || "BACKEND_LOGIN_FAILED",
          errorMessage: backendText || "Backend login failed",
          hataAciklamaTr: backendText
            ? `Sunucu yanıtı: ${backendText}`
            : "Giriş isteği sunucu tarafından reddedildi.",
        });
        // Her hatalı girişte reCAPTCHA'yı sıfırla ve tekrar göster
        setIsVerified(false);
        setCaptchaToken(null);
        setLoading(false);
        
        if (res.error?.messages?.["GNR-00008"]) {
          setLoginErrorKey("GNR-00008");
          toast.error(t("GNR-00008"));
        } else if (isCaptchaError) {
          setLoginErrorKey("error_messages.captcha_failed");
          toast.error(t("error_messages.captcha_failed"));
          setResetPasswordCounter((c) => c + 1);
        } else {
          setLoginErrorKey("wrong_email_address_or_password");
          toast.error(t("wrong_email_address_or_password"));
          setResetPasswordCounter((c) => c + 1);
        }
        return;
      }

    } catch (err) {
      // Hata durumunda da deneme sayısını artır
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      if (newAttempts >= 5) {
        setShowCaptcha(true);
      }

      const data = err?.data;
      const nestedMsgs = data?.error?.messages || data?.messages;
      const nestedText =
        nestedMsgs && typeof nestedMsgs === "object"
          ? Object.values(nestedMsgs).filter(Boolean).join("; ")
          : "";
      const techMsg = nestedText || err?.message || String(err);
      logLoginFailure("request_failed", {
        email: values?.email,
        superLogin,
        status: err?.status,
        code: err?.code,
        message: err?.message,
        data: err?.data,
        nestedMessages: nestedMsgs,
        error: err,
      });
      reportClientAuthError({
        provider: "Backend",
        stage: authStage,
        failedEndpoint,
        identifier: values?.email ? String(values.email).toLowerCase().trim() : null,
        errorCode:
          nestedMsgs && typeof nestedMsgs === "object"
            ? Object.keys(nestedMsgs)[0]
            : err?.status != null
              ? `HTTP_${err.status}`
              : err?.code || "LOGIN_UNWRAP_ERROR",
        errorMessage: techMsg,
        hataAciklamaTr: nestedText
          ? `Sunucu yanıtı: ${nestedText}`
          : `İstek hatası: ${techMsg}`.slice(0, 2000),
      });
      // Her hatalı girişte reCAPTCHA'yı sıfırla ve tekrar göster
      setIsVerified(false);
      setCaptchaToken(null);
      setLoading(false);

      // Firebase hatalarını kullanıcıya Türkçe mesaj olarak göster
      const isFirebaseError = err?.code?.startsWith('auth/') || 
                             err?.message?.includes('Firebase') ||
                             err?.message?.includes('too-many-requests') ||
                             err?.message?.includes('quota-exceeded') ||
                             err?.message?.includes('network-request-failed');
      
      if (isFirebaseError) {
        const messageKey = loginMessageKeyFromCode(err?.code);
        logLoginFailure("firebase_error_caught", {
          email: values?.email,
          code: err?.code,
          message: err?.message,
          error: err,
        });
        setLoginErrorKey(messageKey);
        toast.error(t(messageKey));
      } else if (nestedMsgs?.["GNR-00008"]) {
        setLoginErrorKey("GNR-00008");
        toast.error(t("GNR-00008"));
      } else if (nestedMsgs?.["GNR-00027"]) {
        setLoginErrorKey("error_messages.captcha_failed");
        toast.error(t("error_messages.captcha_failed"));
      } else {
        setLoginErrorKey("wrong_email_address_or_password");
        toast.error(t("wrong_email_address_or_password"));
      }
      // Şifre alanını temizle
      setResetPasswordCounter((c) => c + 1);
    }
  };

  // Firebase login wrapper - sadece kontrolü yap, toast burada değil üstte gösterilecek
  const handleFirebaseLogin = async (email, password) => {
    try {
      const firebaseRes = await firebaseLogin(email, password);
      // Üst katmanda ele alınacak
      return firebaseRes;
    } catch (error) {
      logLoginFailure("firebase_wrapper", {
        email,
        code: error?.code,
        message: error?.message,
        error,
      });
      return null;
    }
  };



  // Email verification yeniden gönderme fonksiyonu
  const handleResendVerification = async (email) => {
    try {
      setLoading(true);

      // Önce mevcut oturumda kullanıcı var mı kontrol et
      let user = auth.currentUser;

      if (!user || user.email !== email) {
        // Mevcut oturumda kullanıcı yoksa, email ile kullanıcıyı bul
        try {
          // Geçici olarak email ile giriş yap (şifre bilmiyoruz)
          // Bu sadece kullanıcıyı bulmak için
          const userCredential = await signInWithEmailAndPassword(auth, email, "tempPassword");
          user = userCredential.user;
        } catch (tempLoginError) {
          if (tempLoginError.code === 'auth/wrong-password') {
            // Şifre yanlış ama kullanıcı var - bu normal
            // Kullanıcıyı bulmak için başka yöntem dene
            // TODO: hata mesajı geri açılacaktır.
            // toast.error(t("user_info_not_found"));
            toast.error(t("wrong_email_address_or_password"));
            setLoading(false);
            return;
          } else {
            throw tempLoginError;
          }
        }
      }

      // Email verification gönder
      sendEmailVerificationFirebase(user);
      toast.success(t("new_verification_email_sent"));

      // Eğer geçici oturum açıldıysa kapat
      if (user !== auth.currentUser) {
        await signOut(auth);
      }

    } catch (error) {
      console.error("Email verification yeniden gönderme hatası:", error);

      // Firebase hatalarını tespit et
      const isFirebaseError = error?.code?.startsWith('auth/') || 
                             error?.message?.includes('Firebase') ||
                             error?.message?.includes('too-many-requests') ||
                             error?.message?.includes('quota-exceeded') ||
                             error?.message?.includes('network-request-failed');
      
      if (isFirebaseError) {
        // Firebase hatalarını sadece log'a yaz, kullanıcıya gösterme
        console.error('[Login] Firebase email verification hatası (kullanıcıya gösterilmedi):', {
          code: error?.code,
          message: error?.message,
          error
        });
      } else {
        toast.error(t("verification_email_send_failed"));
      }
    } finally {
      setLoading(false);
    }
  };
  //RETURN----------------------
  if (isAuthenticated) return <Navigate to="/" />;
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: appColors.bgMuted,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"50\" cy=\"50\" r=\"1\" fill=\"rgba(0,0,0,0.02)\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>')",
          opacity: 0.5,
        }
      }}
    >
      {loading && <Loading backdrop />}
      <Container 
        maxWidth="sm"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          padding: { xs: "1rem", sm: "2rem" }
        }}
      >
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: "480px",
              borderRadius: "24px",
              overflow: "hidden",
              background: appColors.white,

              position: "relative",
              margin: "0 auto",
            }}
          >
            {/* Header Section - Green */}
            <Box
              sx={{
                background: theme.palette.primary.main,
                padding: "3rem 2rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "1rem",
                position: "relative",
                minHeight: "180px"
              }}
            >
              <IconButton
                onClick={() => navigate(superLogin ? '/login' : '/superlogin')}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 60,
                  color: appColors.white,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  transition: "all 0.2s ease",
                  zIndex: 10,
                }}
              >
                {superLogin ? (
                  <Person sx={{ fontSize: 20 }} />
                ) : (
                  <AdminPanelSettings sx={{ fontSize: 20 }} />
                )}
              </IconButton>
              <IconButton
                onClick={() => navigate('/')}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: appColors.white,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  transition: "all 0.2s ease",
                  zIndex: 10,
                }}
              >
                <Close sx={{ fontSize: 20 }} />
              </IconButton>

              {/* Logo placeholder - stethoscope icon */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: appColors.white
                }}
              >
                <Security sx={{ fontSize: 28 }} />
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: appColors.white,
                    fontWeight: 700,
                    fontSize: "2rem",
                    lineHeight: 1.2,
                    marginBottom: "0.5rem"
                  }}
                >
                  {t("medilya")}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: appColors.white,
                    fontWeight: 600,
                    fontSize: "1.25rem",
                    margin: 0,
                    opacity: 0.9
                  }}
                >
                  {superLogin ? t("super_login") : t("doctor_login")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: appColors.white,
                    fontSize: "0.875rem",
                    margin: 0,
                    opacity: 0.8,
                    marginTop: "0.25rem"
                  }}
                >
                  {t("secure_access_subtitle")}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "0.75rem",
                    marginTop: "0.5rem",
                    display: "block",
                    maxWidth: "320px"
                  }}
                >
                  Medilya, danışan ve uzmanların randevu, görüşme ve sağlık kayıtlarını yönettiği sağlık yönetim platformudur.
                </Typography>
              </Box>
            </Box>

            {/* Main Form Section - White */}
            <Box
              sx={{
                padding: "2rem",
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem"
              }}
            >
              {!showEmailForm && !superLogin ? (
                // Phone Number Form - Sadece normal login'de göster
                <PhoneNumberForm
                  setLoading={setLoading}
                  superLogin={superLogin}
                  showCaptcha={showCaptcha}
                  siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  isCaptchaVerified={isVerified}
                  captchaToken={captchaToken}
                  onCaptcha={(token) => {
                    console.log('🔍 [Login] Phone onCaptcha called with token:', !!token, 'token:', token ? 'exists' : 'null');
                    startTransition(() => {
                      setIsVerified(!!token);
                      setCaptchaToken(token);
                    });
                    console.log('🔍 [Login] Phone isVerified set to:', !!token);
                  }}
                  onPhoneNumberChange={(phoneNumber) => {
                    // Telefon numarası tamamen girildiğinde (10 haneli) Captcha göster
                    const cleanPhoneNumber = phoneNumber.replace(/\s/g, '');
                    const isPhoneComplete = cleanPhoneNumber.length === 10 && /^[0-9]+$/.test(cleanPhoneNumber);
                    if (isPhoneComplete) {
                      // Eğer captcha zaten gösteriliyorsa ve doğrulanmışsa, sıfırlama
                      if (!showCaptcha) {
                        setShowCaptcha(true);
                        setIsVerified(false);
                        setCaptchaToken(null);
                      }
                      // Eğer captcha gösteriliyorsa ama doğrulanmamışsa, sıfırlama (kullanıcı hala doğruluyor olabilir)
                    } else if (!isPhoneComplete) {
                      setShowCaptcha(false);
                      setIsVerified(false);
                      setCaptchaToken(null);
                    }
                  }}
                />
              ) : (
                // Email Form - SuperLogin'de her zaman göster
                <Box>

                  <EmailForm
                    handleLogin={handleLoginEmail}
                    loading={loading}
                    showCaptcha={showCaptcha}
                    siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    isVerified={isVerified}
                    onCaptcha={(token) => {
                      console.log('🔍 [Login] onCaptcha called with token:', !!token, 'token:', token ? 'exists' : 'null');
                      startTransition(() => {
                        setIsVerified(!!token);
                        setCaptchaToken(token);
                      });
                      console.log('🔍 [Login] isVerified set to:', !!token);
                    }}
                    onResendVerification={handleResendVerification}
                    showVerificationButton={showVerificationButton}
                    onEmailChange={() => setShowVerificationButton(false)}
                    resetPasswordCounter={resetPasswordCounter}
                    loginErrorKey={loginErrorKey}
                    onCredentialEdit={() => setLoginErrorKey(null)}
                    superLogin={superLogin}
                    onForgotPassword={() => setForgotOpen(true)}
                    onFormValuesChange={(email, password) => {
                      if (superLogin) return;

                      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                      const isValidEmail = emailRegex.test(email);
                      const isValidPassword = password && password.length >= 8;
                      const emailChanged = email !== lastEmail;
                      const passwordChanged = password !== lastPassword;

                      if (emailChanged) setLastEmail(email);
                      if (passwordChanged) setLastPassword(password);

                      if ((!isValidEmail || !isValidPassword) && loginAttempts < 5) {
                        setShowCaptcha(false);
                        setIsVerified(false);
                        setCaptchaToken(null);
                      }
                    }}
                  />
                </Box>
              )}


              {/* Divider and Alternative Options - Show only for normal login */}
              {!superLogin && (
                <>
                  {/* Divider */}
                  <Box sx={{ position: "relative", textAlign: "center", margin: "0.5rem 0" }}>
                    <Divider />
                    <Typography
                      variant="body2"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: appColors.white,
                        padding: "0 1rem",
                        color: appColors.gray500,
                        fontSize: "0.875rem"
                      }}
                    >
                      {t("or")}
                    </Typography>
                  </Box>

                  {/* Email Login Button - Show for phone form */}
                  {!showEmailForm && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Email />}
                      sx={{
                        border: `1px solid ${appColors.gray200}`,
                        borderRadius: "12px",
                        padding: "0.875rem",
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        color: appColors.gray900,
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: "rgba(13, 148, 136, 0.04)",
                        },
                        transition: "all 0.2s ease"
                      }}
                      onClick={() => setShowEmailForm(true)}
                    >
                      {t("login_with_email")}
                    </Button>
                  )}

                  {/* Phone Login Button - Show for email form */}
                  {showEmailForm && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<LocalPhone />}
                      sx={{
                        border: `1px solid ${appColors.gray200}`,
                        borderRadius: "12px",
                        padding: "0.875rem",
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        color: appColors.gray900,
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: "rgba(13, 148, 136, 0.04)",
                        },
                        transition: "all 0.2s ease"
                      }}
                      onClick={() => setShowEmailForm(false)}
                    >
                      {t("login_with_phone")}
                    </Button>
                  )}

                  {/* Register Button */}
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonAdd />}
                    sx={{
                      border: `1px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      padding: "0.875rem",
                      fontSize: "1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      color: theme.palette.primary.main,
                      "&:hover": {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: "rgba(13, 148, 136, 0.08)",
                        color: theme.palette.primary.dark,
                      },
                      transition: "all 0.2s ease"
                    }}
                    onClick={() => navigate('/register/doctor')}
                  >
                    {t("register")}
                  </Button>
                </>
              )}

              {/* Security Information */}
              <Box
                sx={{
                  marginTop: "1.5rem",
                  padding: "1rem",
                  background: appColors.bgMuted,
                  borderRadius: "12px",
                  textAlign: "center"
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: appColors.gray500,
                    fontSize: "0.75rem",
                    marginBottom: "0.75rem"
                  }}
                >
                  {t("secure_connection")}
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" flexWrap="wrap">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    <Security sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                    <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                      {t("ssl_security")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    <Lock sx={{ fontSize: 16, color: appColors.gray500 }} />
                    <Typography variant="caption" sx={{ color: appColors.gray500, fontWeight: 600 }}>
                      {t("encryption")}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ marginTop: 1, flexWrap: "wrap", gap: 1 }}>
                  <Typography
                    component={Link}
                    to="/"
                    variant="caption"
                    sx={{ color: theme.palette.primary.main, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    Ana sayfaya dön
                  </Typography>
                  <Typography component="span" variant="caption" sx={{ color: appColors.gray500 }}>|</Typography>
                  <Typography
                    component={Link}
                    to="/gizlilik-politikasi"
                    variant="caption"
                    sx={{ color: theme.palette.primary.main, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    Gizlilik Politikası
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>

      <Dialog
        open={forgotOpen}
        onClose={() => !forgotLoading && setForgotOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'center' } }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: '90%', sm: 520 },
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          {t("forgot_password")}
          <IconButton
            size="small"
            onClick={() => setForgotOpen(false)}
            disabled={forgotLoading}
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography sx={{ color: appColors.gray500, mb: 2 }}>
            {t("reset_password_text")}
          </Typography>
          <TextField
            id="forgot-email"
            name="email"
            label={t("email")}
            fullWidth
            value={forgotFormik.values.email}
            onChange={forgotFormik.handleChange}
            onBlur={forgotFormik.handleBlur}
            error={Boolean(forgotFormik.errors.email) && forgotFormik.touched.email}
            helperText={forgotFormik.touched.email && forgotFormik.errors.email}
            InputProps={{
              startAdornment: <Email sx={{ color: theme.palette.primary.main, mr: 1 }} />
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") forgotFormik.handleSubmit();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button
            variant="text"
            onClick={() => setForgotOpen(false)}
            disabled={forgotLoading}
            sx={{ textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={forgotFormik.handleSubmit}
            disabled={forgotLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,

              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,

              }
            }}
          >
            {forgotLoading ? t("sending") : t("send_reset_email")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
